import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { categoryLabels } from "@/mocks/articles";
import { formatViews, shortDate } from "@/utils/format";

const categoryStyles: Record<Article["category"], string> = {
  gossip: "bg-accent-50 text-accent-700 border-accent-200",
  black: "bg-primary-50 text-primary-700 border-primary-200",
  news: "bg-secondary-100 text-secondary-800 border-secondary-200",
};

interface ArticleCardProps {
  article: Article;
  variant?: "grid" | "list" | "feature";
  showViews?: boolean;
}

export default function ArticleCard({ article, variant = "grid", showViews = false }: ArticleCardProps) {
  const badge = (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap ${categoryStyles[article.category]}`}
    >
      {categoryLabels[article.category]}
    </span>
  );

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 md:w-44 md:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5">
            {badge}
            <span className="text-[12px] text-foreground-400">{shortDate(article.date)}</span>
          </div>
          <h3 className="font-heading text-[15px] md:text-base font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="hidden sm:block text-[13px] text-foreground-500 leading-6 mt-1.5 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-3 text-[12px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full h-48 md:h-56 overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <div className="p-4 md:p-5 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-2">{badge}</div>
          <h3 className="font-heading text-lg font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-[13px] text-foreground-500 leading-6 mt-2 line-clamp-2">{article.excerpt}</p>
          <div className="mt-auto pt-3 flex items-center justify-between text-[12px] text-foreground-400">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative w-full h-40 md:h-44 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 黑料吃瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
        />
        <span className="absolute top-2.5 left-2.5">{badge}</span>
      </div>
      <div className="p-3.5 md:p-4 flex flex-col flex-1">
        <h3 className="font-heading text-[15px] font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-[12.5px] text-foreground-500 leading-6 mt-1.5 line-clamp-2">{article.excerpt}</p>
        <div className="mt-auto pt-3 flex items-center justify-between text-[12px] text-foreground-400">
          <span>{shortDate(article.date)}</span>
          {showViews && (
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <i className="ri-heart-3-line" />
            {formatViews(article.likes)}
          </span>
        </div>
      </div>
    </Link>
  );
}